import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { fetchRolesStart, fetchRolesSuccess, fetchRolesFailure } from '../../../redux/slices/rolesSlice';
import { 
  Box, 
  Typography, 
  Paper, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  Button,
  IconButton,
  Tooltip,
  TextField,
  InputAdornment,
  TablePagination,
  Chip,
  CircularProgress,
  Alert,
  useMediaQuery,
  useTheme
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import { getRoles } from '../../../Action/api';

const Roles = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const dispatch = useDispatch();
  
  const { data: roles, loading, error } = useSelector((state) => state.roles);
  
  const [filteredRoles, setFilteredRoles] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    if (roles.length === 0) {
      fetchRoles();
    } else {
      setFilteredRoles(roles);
    }
  }, []);

  useEffect(() => {
    setFilteredRoles(roles);
  }, [roles]);

  const fetchRoles = async () => {
    try {
      dispatch(fetchRolesStart());
      const data = await getRoles();
      dispatch(fetchRolesSuccess(data));
    } catch (err) {
      dispatch(fetchRolesFailure('Failed to fetch roles. Please try again.'));
      console.error('Error fetching roles:', err);
    }
  };

  useEffect(() => {
    const filtered = roles.filter((role) =>
      role.roleName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      role.roleId.toLowerCase().includes(searchTerm.toLowerCase()) ||
      role.depId.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredRoles(filtered);
    setPage(0);
  }, [searchTerm, roles]);

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const paginatedRoles = filteredRoles.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Box sx={{ 
      p: { xs: 1, sm: 2 }, 
      maxWidth: { xs: '100vw', md: 'calc(100vw - 280px)' }, 
      mx: 'auto',
      width: '100%'
    }}>
      <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between', 
        alignItems: { xs: 'stretch', sm: 'center' }, 
        mb: 2,
        gap: { xs: 1, sm: 0 }
      }}>
        <Typography 
          variant={isMobile ? 'h6' : 'h5'} 
          component="h1" 
          sx={{ fontWeight: 600 }}
        >
          Roles Management
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button 
            variant="outlined" 
            startIcon={<RefreshIcon />}
            size="small"
            onClick={fetchRoles}
            disabled={loading}
            sx={{ textTransform: 'none' }}
          >
            {!isMobile && 'Reload'}
          </Button>
          <Button 
            variant="contained" 
            startIcon={!isMobile && <AddIcon />}
            size="small"
            fullWidth={isMobile}
            sx={{ textTransform: 'none' }}
          >
            Add Role
          </Button>
        </Box>
      </Box>

      <Paper sx={{ p: 2, mb: 2 }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Search by role name, role ID, or department ID..."
          value={searchTerm}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Paper>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
      ) : (
        <>
          <TableContainer 
            component={Paper} 
            sx={{ 
              boxShadow: 2,
              overflowX: 'auto',
              width: '100%',
              '&::-webkit-scrollbar': {
                height: '8px',
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: '#888',
                borderRadius: '4px',
              }
            }}
          >
            <Table size="small" sx={{ minWidth: { xs: 600, sm: 650 } }}>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.875rem' }}>S.No</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.875rem' }}>Role ID</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.875rem' }}>Role Name</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.875rem' }}>Department ID</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.875rem' }}>Level ID</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.875rem' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedRoles.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                      <Typography variant="body2" color="text.secondary">
                        No roles found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedRoles.map((role, index) => (
                    <TableRow 
                      key={role.id}
                      sx={{ 
                        '&:hover': { backgroundColor: '#f9f9f9' },
                        '&:nth-of-type(even)': { backgroundColor: '#fafafa' }
                      }}
                    >
                      <TableCell sx={{ fontSize: '0.813rem' }}>{page * rowsPerPage + index + 1}</TableCell>
                      <TableCell sx={{ fontSize: '0.813rem' }}>
                        <Chip label={role.roleId} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.813rem', fontWeight: 500 }}>{role.roleName}</TableCell>
                      <TableCell sx={{ fontSize: '0.813rem' }}>{role.depId}</TableCell>
                      <TableCell sx={{ fontSize: '0.813rem' }}>{role.levelId}</TableCell>
                      <TableCell align="right">
                        <Tooltip title="Edit">
                          <IconButton size="small" color="primary">
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton size="small" color="error">
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component={Paper}
            sx={{ 
              mt: 1, 
              boxShadow: 1,
              '.MuiTablePagination-toolbar': {
                flexWrap: { xs: 'wrap', sm: 'nowrap' },
                justifyContent: 'center'
              },
              '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': {
                fontSize: { xs: '0.75rem', sm: '0.875rem' }
              }
            }}
            rowsPerPageOptions={[5, 10, 25, 50]}
            count={filteredRoles.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage={isMobile ? 'Rows:' : 'Rows per page:'}
          />
        </>
      )}
    </Box>
  );
};

export default Roles;
